// Hooks CRUD genéricos sobre la API REST. Cada módulo comparte el shape
// de lista ({ rows, total }) y de detalle ({ <singular>, access }).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api.ts";

export type Row = Record<string, unknown>;

export interface ListResult {
  rows: Row[];
  total: number;
  page?: number;
  pageSize?: number;
}

export interface ListParams { page?: number; pageSize?: number; q?: string; extra?: Record<string, string> }

/** GET /api/v1/<path>?page&pageSize&q[&extra…] → { rows, total, page, pageSize }. */
export function useList(path: string, params: ListParams = {}, enabled = true) {
  const search = new URLSearchParams();
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("pageSize", String(params.pageSize));
  if (params.q) search.set("q", params.q);
  for (const [k, v] of Object.entries(params.extra ?? {})) if (v) search.set(k, v);
  const qs = search.toString();
  return useQuery<ListResult>({
    queryKey: ["list", path, params.page ?? 1, params.pageSize ?? 25, params.q ?? "", params.extra ?? {}],
    queryFn: () => api.get<ListResult>(`/${path}${qs ? `?${qs}` : ""}`),
    enabled,
    placeholderData: (prev) => prev, // mantiene la página anterior mientras carga (sin parpadeo)
  });
}

/** GET /api/v1/<path>/:id → { [singular]: row, access }. */
export function useRecord(path: string, id: string | undefined, singular: string) {
  return useQuery<{ row: Row; access: string | undefined }>({
    queryKey: ["record", path, id],
    enabled: Boolean(id),
    queryFn: async () => {
      const r = await api.get<Record<string, unknown>>(`/${path}/${id}`);
      return { row: (r[singular] as Row) ?? {}, access: r["access"] as string | undefined };
    },
  });
}

export function useCreate(path: string, singular: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Row) => api.post<Record<string, unknown>>(`/${path}`, body),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["list", path] });
      return (r[singular] as Row) ?? r;
    },
  });
}

export function useUpdate(path: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Row }) =>
      api.patch<Record<string, unknown>>(`/${path}/${id}`, body),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ["list", path] });
      void qc.invalidateQueries({ queryKey: ["record", path, vars.id] });
    },
  });
}

export function useDelete(path: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/${path}/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["list", path] });
    },
  });
}
